/**
 * The main interactive component for enabling Student Helper on the teacher side.
 */

import { useEffect, useMemo, useState } from "react"

import { useDebounce } from "react-use"
import { formatDistanceToNow } from "date-fns"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CheckSquare, Info, RefreshCw, Search, Square } from "lucide-react"

import { Spinner } from "@/components/ui/Spinner"
import { Button } from "@/components/ui/Button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover"

import {
  StudentHelperAssignmentPreparationStatus,
  StudentHelperPreparationStatus,
} from "../../../../models/StudentHelperAssignmentPreparationStatus"
import { StudentHelperButton } from "../StudentHelperButton/StudentHelperButton"
import { StudentHelperTeacherController } from "../../StudentHelperTeacher.controller"

function useTimeAgo(startTime?: Date | null): string {
  const [timeAgo, setTimeAgo] = useState("...")

  useEffect(() => {
    if (!startTime) return
    const update = () => {
      setTimeAgo(formatDistanceToNow(startTime, { addSuffix: true }))
    }
    update()
    const interval = setInterval(update, 1000)

    return () => clearInterval(interval)
  }, [startTime])

  return timeAgo
}

interface StatusIndicatorProps {
  status: StudentHelperPreparationStatus
  isError: boolean
  startTime?: Date | null
  completeTime?: Date | null
  onRestartPreparation?: () => void
}

function StatusIndicator({
  status,
  isError,
  startTime,
  completeTime,
  onRestartPreparation,
}: StatusIndicatorProps) {
  const timeAgo = useTimeAgo(startTime)
  let formattedDate

  const getStatusDetails = (isError: boolean) => {
    if (isError) {
      return {
        icon: <RefreshCw size={12} />,
        header: "Error Preparing Assignment",
        description: "Please try again",
      }
    }
    switch (status) {
      case "processing":
        return {
          icon: <Spinner className="tw-text-secondary" />,
          header: "Assignment analyzing",
          description: `Started: ${timeAgo}`,
        }
      case "completed":
        formattedDate = completeTime
          ? completeTime.toLocaleString(undefined, {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            })
          : "N/A"
        return {
          icon: <RefreshCw size={12} />,
          header: "Assignment analyzed",
          description: `Completed: ${formattedDate}`,
        }
      default:
        return {
          icon: <Search size={12} />,
          header: "Assignment not analyzed",
          description: "Last analyzed: never",
        }
    }
  }
  const { icon, header, description } = getStatusDetails(isError)

  const isRestartable = status === "completed"

  return (
    <div className="tw-flex tw-items-center tw-mt-3">
      {isRestartable ? (
        <Button
          variant="outline_grey"
          size="icon"
          onClick={onRestartPreparation}
          disabled={!isRestartable}
          aria-label="Restart preparation"
        >
          {icon}
        </Button>
      ) : (
        <div className="tw-rounded-full tw-bg-muted tw-text-secondary tw-p-3">
          {icon}
        </div>
      )}
      <div className="tw-ml-2">
        <div className="tw-text-sm tw-font-bold">{header}</div>
        <div className="tw-text-xs">{description}</div>
      </div>
    </div>
  )
}

/**
 * Hook for updating status
 */
function useStudentHelperStatus(controller: StudentHelperTeacherController) {
  const queryClient = useQueryClient()
  const queryKey = ["studentHelper", "status", controller.assignmentId]

  // 1. Query: Fetches initial status
  const statusQuery = useQuery<StudentHelperAssignmentPreparationStatus>({
    queryKey,
    queryFn: () => controller.getPreparationStatus(),
    enabled: !!controller.assignmentId,
    staleTime: Infinity,
  })

  // 2. Mutation: Handles the "Prepare" button click
  const prepareMutation = useMutation({
    mutationFn: () => controller.startPreparation(),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data)
    },
    onError: () => {
      setAllowInstantHelper(false)
    },
  })

  // 3. Mutation: Handles the "Re-prepare" (sync icon) button click
  const restartMutation = useMutation({
    mutationFn: () => controller.restartPreparation(),
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data)
    },
    onError: () => {
      setAllowInstantHelper(false)
    },
  })

  // Manages the `allowInstantHelper` toggle state.
  const [allowInstantHelper, setAllowInstantHelper] = useState(false)

  // Query to fetch the initial state of allowInstantHelper.
  useQuery<boolean>({
    queryKey: [
      "studentHelper",
      "allowInstantHelper",
      controller.assignmentId,
      controller.rosterId,
    ],
    queryFn: async () => {
      const initialAllowInstantHelper =
        await controller.getInitialAllowInstantHelper()
      setAllowInstantHelper(initialAllowInstantHelper)
      return initialAllowInstantHelper
    },
    enabled: controller.isRosterAssigned,
    refetchOnWindowFocus: false,
  })

  // 4. Mutation: Handles toggling the 'allow' checkbox/button.
  const toggleAllowMutation = useMutation({
    mutationFn: (currentValue: boolean) =>
      controller.setAllowInstantHelper(!currentValue),
    onSuccess: (data) => {
      setAllowInstantHelper((prev) => !prev) // Update state when toggled
    },
  })

  // 5. Unified state
  const combinedState = useMemo(() => {
    const data = statusQuery.data
    const isPreparing =
      prepareMutation.isPending ||
      restartMutation.isPending ||
      statusQuery.isLoading
    const error =
      statusQuery.error || prepareMutation.error || restartMutation.error
    const isError = !!error

    const status: StudentHelperPreparationStatus = isPreparing
      ? "processing"
      : (data?.status ?? "inactive")

    return {
      status,
      isError,
      startTime: data?.created ? new Date(data.created) : null,
      completeTime: data?.updated ? new Date(data.updated) : null,
      isPreparing,
      isToggling: toggleAllowMutation.isPending,
      allowInstantHelper,
      isRosterAssigned: controller.isRosterAssigned,
    }
  }, [
    statusQuery.data,
    statusQuery.error,
    statusQuery.isLoading,
    prepareMutation.isPending,
    prepareMutation.error,
    restartMutation.isPending,
    restartMutation.error,
    toggleAllowMutation.isPending,
    allowInstantHelper,
    controller.isRosterAssigned,
  ])

  return {
    ...combinedState,
    runPrepare: prepareMutation.mutate,
    runRestart: restartMutation.mutate,
    runToggleAllow: () => toggleAllowMutation.mutate(allowInstantHelper),
  }
}

interface StudentHelperMenuProps {
  controller: StudentHelperTeacherController
  onShowInfo: () => void
}

export function StudentHelperMenu({
  controller,
  onShowInfo,
}: StudentHelperMenuProps) {
  const [isHovering, setIsHovering] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)

  useDebounce(
    () => {
      setIsMenuOpen(isHovering)
    },
    100,
    [isHovering],
  )

  const {
    status,
    isError,
    startTime,
    completeTime,
    isPreparing,
    isToggling,
    allowInstantHelper,
    isRosterAssigned,
    runPrepare,
    runRestart,
    runToggleAllow,
  } = useStudentHelperStatus(controller)

  function handleMainButtonClick() {
    if (status === "inactive" || isError) {
      runPrepare()
      if (!allowInstantHelper) {
        runToggleAllow()
      }
    } else if (status === "completed" && isRosterAssigned) {
      runToggleAllow()
    }
  }

  function handleMouseEnter() {
    setIsHovering(true)
  }

  function handleMouseLeave() {
    setIsHovering(false)
  }

  return (
    <Popover open={isMenuOpen} onOpenChange={setIsHovering}>
      <PopoverTrigger
        asChild
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <StudentHelperButton
          status={status}
          isError={isError}
          allowInstantHelper={allowInstantHelper}
          isPending={isPreparing || isToggling}
          onClick={handleMainButtonClick}
        />
      </PopoverTrigger>
      <PopoverContent
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="tw-w-[280px] tw-px-8 tw-py-6"
      >
        <div role="dialog" aria-label="Student Helper Menu">
          <div className="tw-flex tw-items-center tw-mb-2">
            <h3 className="tw-text-xl tw-font-bold tw-text-foreground">
              Student Helper
            </h3>

            <Button
              size="icon"
              variant="ghost"
              className="tw-ml-2"
              onClick={onShowInfo}
              aria-label="More information"
            >
              <Info size={16} className="tw-text-primary" />
            </Button>
          </div>
          <p className="tw-text-sm tw-mb-3">
            Student Helper is an AI assistant for you and your classroom. It can
            help your students without giving them the answer.
          </p>

          {isRosterAssigned && (
            <div className="tw-py-1">
              <Button
                variant="secondary"
                className="tw-w-full tw-rounded-sm"
                onClick={handleMainButtonClick}
                disabled={isPreparing || isToggling || status === "processing"}
                aria-checked={allowInstantHelper || status === "processing"}
              >
                {allowInstantHelper || status === "processing" ? (
                  <CheckSquare />
                ) : (
                  <Square />
                )}
                Allow Student Helper
              </Button>
            </div>
          )}

          <StatusIndicator
            status={status}
            isError={isError}
            startTime={startTime}
            completeTime={completeTime}
            onRestartPreparation={runRestart}
          />
        </div>
      </PopoverContent>
    </Popover>
  )
}
